/**
 * Add Device Profile Composer
 */

import { useAppForm } from '@/hooks/app-form'
import { useApiMutation } from '@/api/ApiQueryServiceProvider'
import { useMemo } from 'react'
import {
  AddDeviceProfileFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type AddDeviceProfileFormData,
} from './schema'

export {
  AddDeviceProfileFormSchema,
  transformToApiRequest,
  type AddDeviceProfileFormData,
}
import { FormConfigLoader } from '@/lib/formConfigLoader'
import {
  renderControl,
  renderButtonControl,
  renderCheckboxControl,
  type ControlConfig
} from '../../formRenderFunctions'
import { coerceFieldValue } from '@/lib/utils'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Layers } from 'lucide-react'
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)

export type AddDeviceProfileComposerProps = {
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  initialValues?: Partial<AddDeviceProfileFormData>
  showHeader?: boolean
  submitLabel?: string
}

const validateField = (name: keyof AddDeviceProfileFormData, value: unknown): string | null => {
  const shape = AddDeviceProfileFormSchema.shape[name]
  if (!shape) return null

  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

export function AddDeviceProfileComposer(props: AddDeviceProfileComposerProps) {
  const { onSuccess, onError, onCancel, initialValues, showHeader = true, submitLabel } = props

  const controlsByGroup = useMemo(() => FormConfigLoader.getControlsByGroup(FORM_CONFIG), [])
  const groups = useMemo(() => FormConfigLoader.getGroups(FORM_CONFIG), [])

  const addProfile = useApiMutation('upsertDeviceProfile', 'POST', {
    invalidates: ['getDeviceProfiles'],
    onSuccess: () => {
      onSuccess?.()
    },
    onError: (error) => {
      onError?.(error)
    }
  })

  const form = useAppForm({
    schema: AddDeviceProfileFormSchema,
    defaultValues: {
      deviceProfileName: initialValues?.deviceProfileName ?? '',
      deviceProfileNetwork: initialValues?.deviceProfileNetwork ?? 'LoRa',
      provisioningRequired: initialValues?.provisioningRequired ?? 'yes',
      region: initialValues?.region ?? '',
      macVersion: initialValues?.macVersion ?? '',
      supportsOtaa: initialValues?.supportsOtaa ?? false,
      supportsClassB: initialValues?.supportsClassB ?? false,
    },
    onSubmit: async ({ value }) => {
      const formValue = value as AddDeviceProfileFormData
      log.debug('[AddDeviceProfileComposer] Submitting form:', formValue)
      const coerced = {
        deviceProfileName: (formValue as any).deviceProfileName,
        deviceProfileNetwork: (formValue as any).deviceProfileNetwork,
        provisioningRequired: (formValue as any).provisioningRequired,
        region: (formValue as any).region,
        macVersion: (formValue as any).macVersion,
        supportsOtaa: (formValue as any).supportsOtaa,
        supportsClassB: (formValue as any).supportsClassB,
      }

      const parsed = AddDeviceProfileFormSchema.safeParse(coerced)

      if (!parsed.success) {
        log.error('[AddDeviceProfileComposer] Validation failed:', parsed.error)
        // Handle errors
        return
      }

      log.debug('[AddDeviceProfileComposer] Validation success, sending request...')
      const apiRequest = transformToApiRequest(parsed.data)
      await addProfile.mutateAsync(apiRequest)
    }
  })

  const formBody = (
    <div className="space-y-8">
          {groups.map((group) => (
            <div key={group.formGroupName} className="space-y-4">
              <div className="grid gap-4">
                {controlsByGroup.get(group.formGroupName)?.map((controlConfig) => (
                  <form.AppField
                    key={controlConfig.name}
                    name={controlConfig.name as any}
                    validators={{
                      onChange: ({ value }) =>
                        validateField(
                          controlConfig.name as keyof AddDeviceProfileFormData,
                          coerceFieldValue(controlConfig.name, value)
                        ) || undefined,
                      onBlur: ({ value }) =>
                        validateField(
                          controlConfig.name as keyof AddDeviceProfileFormData,
                          coerceFieldValue(controlConfig.name, value)
                        ) || undefined,
                    }}
                  >
                    {(bound) => {
                      const config: ControlConfig = {
                        name: controlConfig.name,
                        label: controlConfig.label,
                        placeholder: controlConfig.placeholder,
                        required: true,
                        visibility: controlConfig.visibility,
                        disabled: controlConfig.disabled,
                        type: controlConfig.inputType,
                        options: controlConfig.selectOptions as any,
                        htmlControl: controlConfig.htmlControl
                      }

                      if (controlConfig.htmlControl === 'checkbox') {
                        return renderCheckboxControl(config, bound)
                      }

                      return renderControl(config, bound)
                    }}
                  </form.AppField>
                ))}
              </div>
            </div>
          ))}

          <div className="flex justify-end gap-4">
            {renderButtonControl({
              label: FORM_CONFIG.cancelButton?.label ?? '',
              variant: 'outline',
              onClick: onCancel,
              visibility: !!onCancel,
              type: 'button'
            })}
            {renderButtonControl(
              {
                label: (addProfile.isPending
                  ? FORM_CONFIG.submitButton?.loadingLabel
                  : (submitLabel ?? FORM_CONFIG.submitButton?.label)) ?? '',
                variant: 'default',
                type: 'submit'
              },
              { SubmitButton: form.SubmitButton }
            )}
          </div>
        </div>
  )

  return (
    <form.AppForm>
      {showHeader ? (
        <div className="space-y-4">
          <Card className="shadow-sm pb-0 overflow-hidden">
            <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Layers className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">{FORM_CONFIG.formTitle}</CardTitle>
                  <CardDescription>{FORM_CONFIG.formDescription}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="pt-6">{formBody}</CardContent>
          </Card>
        </div>
      ) : (
        formBody
      )}
    </form.AppForm>
  )
}
